import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  List as ListIcon,
  Plus,
  Trash2,
  Pencil,
  MapPin,
  Clock,
  StickyNote,
  Users as UsersIcon,
  Copy,
  Printer,
  AlertTriangle,
  Grip,
  X,
  Mail,
  Save,
  Wand2,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

// ---------- helpers ----------
const ymdLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const localToISO = (s) => (s ? new Date(s).toISOString() : null);
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

const hoursBetween = (a, b) => Math.max(0, (new Date(b) - new Date(a)) / 36e5);
const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay()); // Sunday start
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Returns true if two ISO ranges overlap (a_start < b_end && b_start < a_end)
const rangesOverlap = (a1, a2, b1, b2) =>
  new Date(a1) < new Date(b2) && new Date(b1) < new Date(a2);

const blankShift = {
  id: null,
  location_id: "",
  employee_id: null,
  employee_name: "",
  role: "",
  start_at: "",
  end_at: "",
  notes: "",
};

// ---------- Draggable employee chip (pool sidebar + calendar cells) ----------
function EmployeeChip({ employee, onEdit, isAdminOrManager }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `emp-${employee.id}`,
    data: { type: "employee", employee },
    disabled: !isAdminOrManager,
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={`emp-chip-${employee.id}`}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-md border bg-white text-sm group transition-shadow ${
        isDragging ? "opacity-50" : "hover:shadow-sm"
      } ${isAdminOrManager ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{ borderColor: employee.color || "#E4E4E7" }}
    >
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ background: employee.color || "#9CA3AF" }}
      />
      <div className="flex-1 min-w-0" {...attributes} {...listeners}>
        <p className="font-medium truncate leading-tight">{employee.name}</p>
        {employee.role && (
          <p className="text-[10px] text-gray-500 leading-tight truncate">{employee.role}</p>
        )}
      </div>
      {employee.email && (
        <Mail size={11} className="text-gray-400 shrink-0" title={employee.email} />
      )}
      {isAdminOrManager && (
        <button
          type="button"
          aria-label={`Edit ${employee.name}`}
          data-testid={`emp-edit-${employee.id}`}
          onClick={(e) => { e.stopPropagation(); onEdit(employee); }}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-[#0055FF] transition-opacity"
        >
          <Pencil size={12} />
        </button>
      )}
      {isAdminOrManager && (
        <Grip size={12} className="text-gray-300 shrink-0" />
      )}
    </div>
  );
}

// ---------- Draggable shift chip on calendar ----------
function ShiftChip({ shift, employeeColor, onClick, canDrag }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `shift-${shift.id}`,
    data: { type: "shift", shift },
    disabled: !canDrag,
  });
  const bg = employeeColor || "#0055FF";
  return (
    <button
      ref={setNodeRef}
      type="button"
      data-testid={`cal-shift-${shift.id}`}
      onClick={() => onClick?.(shift)}
      {...attributes}
      {...listeners}
      className={`block w-full text-left text-[11px] leading-tight px-1.5 py-1 rounded transition-colors truncate ${
        isDragging ? "opacity-50" : ""
      }`}
      style={{
        background: `${bg}1A`,
        color: bg,
        borderLeft: `3px solid ${bg}`,
      }}
      title={`${shift.employee_name} · ${fmtTime(shift.start_at)}-${fmtTime(shift.end_at)} · ${shift.location_name}`}
    >
      <span className="tabular-nums">{fmtTime(shift.start_at)}</span>{" "}
      <span className="font-medium">{shift.employee_name}</span>
    </button>
  );
}

// ---------- Droppable day cell ----------
function DayCell({ date, dayKey, children, isToday, canManage, forLocationId }) {
  const dropId = forLocationId ? `day-${dayKey}::${forLocationId}` : `day-${dayKey}`;
  const { setNodeRef, isOver } = useDroppable({
    id: dropId,
    data: { type: "day", date, forLocationId },
    disabled: !canManage,
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={`cal-day-${dayKey}${forLocationId ? `-${forLocationId}` : ""}`}
      className={`group border-b border-r border-gray-100 p-1.5 overflow-hidden transition-colors ${
        isToday ? "bg-blue-50/40" : ""
      } ${isOver ? "bg-blue-100/60 outline outline-2 outline-[#0055FF]" : ""}`}
    >
      {children}
    </div>
  );
}

// ---------- Main page ----------
export default function SchedulePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isAdminOrManager = isAdmin || isManager;

  const [locations, setLocations] = useState([]);
  // Empty Set = "all locations". Non-empty = filter to these.
  const [selectedLocationIds, setSelectedLocationIds] = useState(() => new Set());
  const view_default = "week";
  const [view, setView] = useState(view_default); // 'list' | 'week' | 'month'
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [presets, setPresets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Calendar anchor
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [weekAnchor, setWeekAnchor] = useState(() => startOfWeek(new Date()));

  // Employee pool search filter (declared early — referenced by filteredEmployees useMemo below)
  const [empSearch, setEmpSearch] = useState("");

  // Shift dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(blankShift);
  const [saving, setSaving] = useState(false);

  // Employee dialog
  const [empOpen, setEmpOpen] = useState(false);
  const [empForm, setEmpForm] = useState({ id: null, name: "", role: "", phone: "", email: "", notes: "", color: "", location_id: "" });
  const [empSaving, setEmpSaving] = useState(false);

  // Sensors — 6px activation distance so click vs drag is clean
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Selection helpers
  const isAllSelected = selectedLocationIds.size === 0;
  const singleSelectedId = selectedLocationIds.size === 1 ? [...selectedLocationIds][0] : null;
  const toggleLocationId = (id) => {
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loadLocations = useCallback(async () => {
    try {
      const { data } = await api.get("/locations");
      setLocations(data);
      // Default location for non-admin
      if (!isAdmin) {
        if (isManager) {
          const first = (user.managed_location_ids || [])[0];
          if (first) setSelectedLocationIds(new Set([first]));
        } else if (user.location_id) {
          setSelectedLocationIds(new Set([user.location_id]));
        }
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load locations");
    }
  }, [isAdmin, isManager, user]);

  const loadShifts = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch every visible shift (no server-side location filter) so we can
      // accurately badge each location pill with its shift count. Client-side filter below.
      const { data } = await api.get("/shifts");
      setShifts(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load shifts");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      // Always load all employees visible to the user; filter client-side by selection.
      const { data } = await api.get("/employees", { params: { active: true } });
      setEmployees(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load employees");
    }
  }, []);

  const loadPresets = useCallback(async () => {
    try {
      const { data } = await api.get("/shift-presets");
      setPresets(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load shift presets");
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/schedule-templates");
      setTemplates(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load templates");
    }
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);
  useEffect(() => { loadShifts(); }, [loadShifts]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadPresets(); }, [loadPresets]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Filter locations the user can manage (used to default the dialog)
  const manageableLocations = useMemo(() => {
    if (isAdmin) return locations;
    if (isManager) return locations.filter((l) => (user.managed_location_ids || []).includes(l.id));
    return [];
  }, [isAdmin, isManager, locations, user]);

  const canManageAt = (locId) => {
    if (isAdmin) return true;
    if (isManager) return (user.managed_location_ids || []).includes(locId);
    return false;
  };

  // Employee color lookup
  const empColor = (shift) => {
    if (shift.employee_id) {
      const e = employees.find((x) => x.id === shift.employee_id);
      if (e) return e.color;
    }
    return null;
  };

  const locationName = (id) => locations.find((l) => l.id === id)?.name || "—";

  // Visible range based on current view (also used for hours summary + pill badges)
  const visibleRange = useMemo(() => {
    if (view === "month") {
      return [
        new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1),
        new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1),
      ];
    }
    if (view === "week") return [weekAnchor, addDays(weekAnchor, 7)];
    return [new Date(0), new Date(8640000000000000)];
  }, [view, monthAnchor, weekAnchor]);

  // Filter employees by current selection + search (used by the pool sidebar)
  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    return employees.filter((e) => {
      if (!isAllSelected && !selectedLocationIds.has(e.location_id)) return false;
      if (q && !e.name.toLowerCase().includes(q) && !(e.role || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [employees, isAllSelected, selectedLocationIds, empSearch]);

  // Filter shifts: by selected locations AND by visible range
  const filteredShifts = useMemo(() => {
    const [from, to] = visibleRange;
    return shifts.filter((s) => {
      if (!isAllSelected && !selectedLocationIds.has(s.location_id)) return false;
      const d = new Date(s.start_at);
      return d >= from && d < to;
    });
  }, [shifts, isAllSelected, selectedLocationIds, visibleRange]);

  // Per-location count of shifts in the current visible range (for pill badges)
  const countsPerLocation = useMemo(() => {
    const [from, to] = visibleRange;
    const m = new Map();
    for (const s of shifts) {
      const d = new Date(s.start_at);
      if (d < from || d >= to) continue;
      m.set(s.location_id, (m.get(s.location_id) || 0) + 1);
    }
    return m;
  }, [shifts, visibleRange]);

  // Total shift count across all visible locations — memoised to avoid recomputing twice per render.
  const totalShiftCount = useMemo(
    () => Array.from(countsPerLocation.values()).reduce((a, b) => a + b, 0),
    [countsPerLocation],
  );

  // "Coverage gaps" per location vs the most recent template: for every (date, day_of_week)
  // in the visible range, if a template entry expects a shift at this location on that day
  // but the schedule has zero shifts on that exact date+location, it's a gap.
  const gapsPerLocation = useMemo(() => {
    const m = new Map();
    // Only meaningful in Week/Month view, where the range is a fixed calendar window
    if (view === "list" || templates.length === 0) return m;
    const tmpl = templates[0]; // most recent (server sorts by created_at desc)
    if (!tmpl?.entries?.length) return m;
    const [from, to] = visibleRange;

    // Build: locationId -> Set<day_of_week> the template expects
    const expects = new Map();
    for (const e of tmpl.entries) {
      if (!expects.has(e.location_id)) expects.set(e.location_id, new Set());
      expects.get(e.location_id).add(e.day_of_week);
    }
    // Build: locationId -> Set<"YYYY-MM-DD"> already scheduled in this range
    const scheduledDays = new Map();
    for (const s of shifts) {
      const d = new Date(s.start_at);
      if (d < from || d >= to) continue;
      const key = ymdLocal(d);
      if (!scheduledDays.has(s.location_id)) scheduledDays.set(s.location_id, new Set());
      scheduledDays.get(s.location_id).add(key);
    }
    // For each date in [from,to), check each location whose template expects that DOW
    const d = new Date(from);
    while (d < to) {
      const dow = d.getDay();
      const ymd = ymdLocal(d);
      for (const [locId, dowSet] of expects.entries()) {
        if (!dowSet.has(dow)) continue;
        const has = scheduledDays.get(locId)?.has(ymd);
        if (!has) m.set(locId, (m.get(locId) || 0) + 1);
      }
      d.setDate(d.getDate() + 1);
    }
    return m;
  }, [shifts, templates, visibleRange, view]);

  // Group filteredShifts by day
  const shiftsByDay = useMemo(() => {
    const m = new Map();
    for (const s of filteredShifts) {
      const k = ymdLocal(new Date(s.start_at));
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    for (const arr of m.values()) arr.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    return m;
  }, [filteredShifts]);
  const sortedDays = useMemo(() => Array.from(shiftsByDay.keys()).sort(), [shiftsByDay]);

  // Calendar cells for month view
  const monthCells = useMemo(() => {
    const first = new Date(monthAnchor);
    const startWd = first.getDay();
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWd; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(first.getFullYear(), first.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [monthAnchor]);

  // Week cells (7 days from weekAnchor)
  const weekCells = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekAnchor, i)), [weekAnchor]);

  // Hours per employee summary (within current visible range, filtered location)
  const hoursSummary = useMemo(() => {
    const totals = new Map();
    for (const s of filteredShifts) {
      const key = s.employee_id || s.employee_name;
      const cur = totals.get(key) || { name: s.employee_name, color: empColor(s) || "#9CA3AF", hours: 0 };
      cur.hours += hoursBetween(s.start_at, s.end_at);
      totals.set(key, cur);
    }
    return Array.from(totals.values()).sort((a, b) => b.hours - a.hours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredShifts, employees]);

  // Conflict detection — same employee, overlapping ranges
  const findConflict = useCallback(
    (employeeKey, start, end, ignoreId) =>
      shifts.find((s) => {
        if (s.id === ignoreId) return false;
        const k = s.employee_id || s.employee_name;
        if (k !== employeeKey) return false;
        return rangesOverlap(s.start_at, s.end_at, start, end);
      }),
    [shifts],
  );

  // ---------- Shift dialog actions ----------
  const openNew = (preset = {}) => {
    const defaultLoc =
      singleSelectedId && canManageAt(singleSelectedId)
        ? singleSelectedId
        : (manageableLocations[0]?.id || "");
    // Default to TODAY 9 AM – 5 PM if no time preset provided
    let defaults = {};
    if (!preset.start_at && !preset.end_at) {
      const start = new Date(); start.setHours(9, 0, 0, 0);
      const end = new Date(); end.setHours(17, 0, 0, 0);
      defaults = {
        start_at: isoToLocalInput(start.toISOString()),
        end_at: isoToLocalInput(end.toISOString()),
      };
    }
    setEditing({ ...blankShift, location_id: defaultLoc, ...defaults, ...preset });
    setDialogOpen(true);
  };
  const openEdit = (shift) => {
    setEditing({
      id: shift.id,
      location_id: shift.location_id,
      employee_id: shift.employee_id || null,
      employee_name: shift.employee_name,
      role: shift.role || "",
      start_at: isoToLocalInput(shift.start_at),
      end_at: isoToLocalInput(shift.end_at),
      notes: shift.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing.location_id) return toast.error("Pick a location");
    if (!editing.employee_name.trim()) return toast.error("Employee name is required");
    if (!editing.start_at || !editing.end_at) return toast.error("Start and end time are required");
    const startISO = localToISO(editing.start_at);
    const endISO = localToISO(editing.end_at);
    if (new Date(endISO) <= new Date(startISO)) return toast.error("End time must be after start time");

    // Conflict warning (non-blocking)
    const empKey = editing.employee_id || editing.employee_name.trim();
    const conflict = findConflict(empKey, startISO, endISO, editing.id);
    if (conflict) {
      const ok = window.confirm(
        `${editing.employee_name} already has a shift at ${conflict.location_name} from ${fmtTime(conflict.start_at)} to ${fmtTime(conflict.end_at)}. Save anyway?`,
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      const payload = {
        location_id: editing.location_id,
        employee_id: editing.employee_id || null,
        employee_name: editing.employee_name.trim(),
        role: editing.role.trim(),
        start_at: startISO,
        end_at: endISO,
        notes: editing.notes.trim(),
      };
      if (editing.id) {
        await api.patch(`/shifts/${editing.id}`, payload);
        toast.success("Shift updated");
      } else {
        await api.post("/shifts", payload);
        toast.success("Shift added");
      }
      setDialogOpen(false);
      loadShifts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (shift) => {
    if (!window.confirm(`Delete ${shift.employee_name}'s shift on ${fmtDate(shift.start_at)}?`)) return;
    try {
      await api.delete(`/shifts/${shift.id}`);
      toast.success("Shift deleted");
      loadShifts();
    } catch (e) {
      toast.error(formatApiError(e) || "Delete failed");
    }
  };

  // ---------- Drag & drop ----------
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;
    const overType = over.data.current?.type;
    if (overType !== "day") return;
    const dropDate = over.data.current.date;
    const dropForLocationId = over.data.current.forLocationId || null;

    const sourceType = active.data.current?.type;
    if (sourceType === "employee") {
      // Drop an employee into a day -> create a default 9-5 shift.
      // If dropped in a swim-lane cell, schedule at THAT lane's location (manager must control it).
      const employee = active.data.current.employee;
      const targetLocId = dropForLocationId || employee.location_id;
      if (!canManageAt(targetLocId)) {
        return toast.error("You can't schedule at that location");
      }
      const start = new Date(dropDate); start.setHours(9, 0, 0, 0);
      const end = new Date(dropDate); end.setHours(17, 0, 0, 0);
      try {
        await api.post("/shifts", {
          location_id: targetLocId,
          employee_id: employee.id,
          employee_name: employee.name,
          role: employee.role || "",
          start_at: start.toISOString(),
          end_at: end.toISOString(),
          notes: "",
        });
        toast.success(`${employee.name} scheduled ${fmtDate(start.toISOString())} 9-5`);
        loadShifts();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Drop failed");
      }
    } else if (sourceType === "shift") {
      // Move an existing shift to another day (keep time of day).
      // Also move to swim-lane's location if dropped there.
      const shift = active.data.current.shift;
      const targetLocId = dropForLocationId || shift.location_id;
      if (!canManageAt(targetLocId)) return;
      const oldStart = new Date(shift.start_at);
      const oldEnd = new Date(shift.end_at);
      const newStart = new Date(dropDate);
      newStart.setHours(oldStart.getHours(), oldStart.getMinutes(), 0, 0);
      const diff = oldEnd - oldStart;
      const newEnd = new Date(newStart.getTime() + diff);
      // Skip if same day AND same location
      if (ymdLocal(newStart) === ymdLocal(oldStart) && targetLocId === shift.location_id) return;
      try {
        await api.patch(`/shifts/${shift.id}`, {
          location_id: targetLocId,
          start_at: newStart.toISOString(),
          end_at: newEnd.toISOString(),
        });
        toast.success(targetLocId === shift.location_id ? `Moved to ${fmtDate(newStart.toISOString())}` : `Moved to ${locationName(targetLocId)} on ${fmtDate(newStart.toISOString())}`);
        loadShifts();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Move failed");
      }
    }
  };

  // ---------- Employee dialog ----------
  const openEmpNew = () => {
    const defaultLoc = singleSelectedId || (manageableLocations[0]?.id || "");
    setEmpForm({ id: null, name: "", role: "", phone: "", email: "", notes: "", color: "", location_id: defaultLoc });
    setEmpOpen(true);
  };
  const openEmpEdit = (emp) => {
    setEmpForm({ id: emp.id, name: emp.name, role: emp.role || "", phone: emp.phone || "", email: emp.email || "", notes: emp.notes || "", color: emp.color || "", location_id: emp.location_id });
    setEmpOpen(true);
  };
  const saveEmp = async () => {
    if (!empForm.name.trim()) return toast.error("Name is required");
    const targetLoc = empForm.location_id || singleSelectedId || (manageableLocations[0]?.id || "");
    if (!targetLoc) return toast.error("Pick a location first");
    setEmpSaving(true);
    try {
      const payload = {
        name: empForm.name.trim(),
        role: empForm.role.trim(),
        phone: empForm.phone.trim(),
        email: empForm.email.trim(),
        notes: empForm.notes.trim(),
        color: empForm.color || undefined,
      };
      if (empForm.id) {
        await api.patch(`/employees/${empForm.id}`, { ...payload, location_id: targetLoc });
        toast.success("Employee updated");
      } else {
        await api.post("/employees", { ...payload, location_id: targetLoc });
        toast.success("Employee added to pool");
      }
      setEmpOpen(false);
      loadEmployees();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setEmpSaving(false);
    }
  };
  const removeEmp = async () => {
    if (!empForm.id) return;
    if (!window.confirm(`Remove ${empForm.name} from the employee pool?`)) return;
    try {
      await api.delete(`/employees/${empForm.id}`);
      toast.success("Employee removed");
      setEmpOpen(false);
      loadEmployees();
      loadShifts();
    } catch (e) {
      toast.error(formatApiError(e) || "Delete failed");
    }
  };

  const copyWeek = async () => {
    if (!window.confirm("Copy every shift in this week to the next week?")) return;
    try {
      const params = { week_start: weekAnchor.toISOString() };
      if (singleSelectedId) params.location_id = singleSelectedId;
      const { data } = await api.post("/shifts/copy-week", null, { params });
      toast.success(`Copied ${data.copied} shift${data.copied === 1 ? "" : "s"} to next week`);
      // Jump to next week so the user sees it
      setWeekAnchor(addDays(weekAnchor, 7));
      loadShifts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Copy failed");
    }
  };

  const [emailing, setEmailing] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  // Recipients dialog state: employeeId -> bool selected
  const [emailRecipients, setEmailRecipients] = useState({});
  // When 2+ stores are selected, automatically show split-by-store rows on Week view.
  const [splitByStore, setSplitByStore] = useState(true);
  // Collapsed location groups in the employee pool (persisted in localStorage)
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = window.localStorage?.getItem("fixboard.collapsedEmpGroups");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const toggleGroupCollapsed = (locId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(locId)) next.delete(locId);
      else next.add(locId);
      try {
        window.localStorage?.setItem("fixboard.collapsedEmpGroups", JSON.stringify([...next]));
      } catch (err) {
        // Persisting collapsed groups is best-effort; ignore quota / private-mode errors.
        console.debug("collapsedEmpGroups persist failed:", err);
      }
      return next;
    });
  };

  // --- Template handlers ---
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
  const [chosenTemplateId, setChosenTemplateId] = useState("");
  const [applyOverwrite, setApplyOverwrite] = useState(false);
  const [applying, setApplying] = useState(false);

  const saveTemplate = async () => {
    if (!templateName.trim()) return toast.error("Give the template a name");
    setTemplateSaving(true);
    try {
      const payload = { name: templateName.trim(), week_start: weekAnchor.toISOString() };
      if (singleSelectedId) payload.location_id = singleSelectedId;
      const { data } = await api.post("/schedule-templates", payload);
      toast.success(`Saved template "${data.name}" with ${data.entries.length} shift${data.entries.length === 1 ? "" : "s"}`);
      setSaveTemplateOpen(false);
      setTemplateName("");
      loadTemplates();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setTemplateSaving(false);
    }
  };

  const applyTemplate = async () => {
    if (!chosenTemplateId) return toast.error("Pick a template");
    if (applyOverwrite && !window.confirm("Overwrite will DELETE every existing shift in this week before applying. Continue?")) return;
    setApplying(true);
    try {
      const params = { week_start: weekAnchor.toISOString(), overwrite: applyOverwrite };
      const { data } = await api.post(`/schedule-templates/${chosenTemplateId}/apply`, null, { params });
      toast.success(`Created ${data.applied} shift${data.applied === 1 ? "" : "s"} this week`);
      if ((data.skipped || []).length) toast.warning(`Skipped: ${data.skipped.join(", ")}`);
      setApplyTemplateOpen(false);
      setChosenTemplateId("");
      setApplyOverwrite(false);
      loadShifts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/schedule-templates/${t.id}`);
      toast.success("Template deleted");
      if (chosenTemplateId === t.id) setChosenTemplateId("");
      loadTemplates();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  // Preset application + save
  const applyPreset = (p) => {
    // Use the date already in the start input (or today)
    const base = editing.start_at ? new Date(editing.start_at) : new Date();
    base.setHours(0, 0, 0, 0);
    const start = new Date(base); start.setHours(p.start_hour, p.start_min || 0, 0, 0);
    const end = new Date(base); end.setHours(p.end_hour, p.end_min || 0, 0, 0);
    setEditing({
      ...editing,
      start_at: isoToLocalInput(start.toISOString()),
      end_at: isoToLocalInput(end.toISOString()),
    });
  };

  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const saveCurrentAsPreset = async () => {
    if (!presetName.trim()) return toast.error("Give the preset a name");
    if (!editing.start_at || !editing.end_at) return toast.error("Set start and end first");
    const s = new Date(editing.start_at);
    const e = new Date(editing.end_at);
    if (e <= s) return toast.error("End must be after start");
    try {
      await api.post("/shift-presets", {
        name: presetName.trim(),
        start_hour: s.getHours(),
        start_min: s.getMinutes(),
        end_hour: e.getHours(),
        end_min: e.getMinutes(),
      });
      toast.success("Preset saved");
      setSavePresetOpen(false);
      setPresetName("");
      loadPresets();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };
  const deletePreset = async (p) => {
    if (!window.confirm(`Delete the "${p.name}" preset?`)) return;
    try {
      await api.delete(`/shift-presets/${p.id}`);
      toast.success("Preset removed");
      loadPresets();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };
  // Candidates for the email dialog: employees with at least one shift in the current week AND an email on file
  const emailCandidates = useMemo(() => {
    const start = weekAnchor;
    const end = addDays(weekAnchor, 7);
    const byId = new Map();
    for (const s of shifts) {
      if (!s.employee_id) continue;
      const d = new Date(s.start_at);
      if (d < start || d >= end) continue;
      // Respect location selection (don't show candidates from stores not in the current view)
      if (!isAllSelected && !selectedLocationIds.has(s.location_id)) continue;
      if (!byId.has(s.employee_id)) {
        const emp = employees.find((e) => e.id === s.employee_id);
        if (!emp || !emp.email) continue;
        byId.set(s.employee_id, { id: emp.id, name: emp.name, email: emp.email, color: emp.color || "#9CA3AF", location_name: s.location_name, shifts: [] });
      }
      byId.get(s.employee_id).shifts.push(s);
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [shifts, employees, weekAnchor, isAllSelected, selectedLocationIds]);

  const openEmailDialog = () => {
    // Default: pre-select everyone
    const initial = {};
    for (const c of emailCandidates) initial[c.id] = true;
    setEmailRecipients(initial);
    setEmailDialogOpen(true);
  };

  const emailWeek = async () => {
    const chosenIds = Object.entries(emailRecipients).filter(([, v]) => v).map(([k]) => k);
    if (chosenIds.length === 0) return toast.error("Pick at least one recipient");
    setEmailing(true);
    try {
      const params = new URLSearchParams();
      params.set("week_start", weekAnchor.toISOString());
      if (singleSelectedId) params.set("location_id", singleSelectedId);
      chosenIds.forEach((id) => params.append("employee_ids", id));
      const { data } = await api.post(`/shifts/email-week?${params.toString()}`);
      if (data.sent > 0) {
        const names = (data.sent_to || []).map((s) => s.name).join(", ");
        toast.success(`Emailed ${data.sent} ${data.sent === 1 ? "person" : "people"}: ${names}`);
      } else {
        toast.info("No emails were sent.");
      }
      if ((data.errors || []).length) {
        data.errors.slice(0, 3).forEach((err) => toast.error(err));
      }
      setEmailDialogOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  const printSchedule = () => window.print();

  const today = ymdLocal(new Date());
  const monthLabel = monthAnchor.toLocaleString([], { month: "long", year: "numeric" });
  const weekLabel = `${weekAnchor.toLocaleDateString([], { month: "short", day: "numeric" })} – ${addDays(weekAnchor, 6).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <Layout>
        <style>{`
          @media print {
            aside, header, .no-print { display: none !important; }
            main { margin-left: 0 !important; }
          }
        `}</style>
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
          <div className="flex items-end justify-between gap-4 mb-6 flex-wrap no-print">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">
                Schedule {!isAllSelected && (
                  <span className="text-[#0055FF]">
                    · {[...selectedLocationIds].map(locationName).join(" + ")}
                  </span>
                )}
              </p>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Staff shifts</h1>
              <p className="text-sm text-gray-600 mt-2">
                {isAllSelected
                  ? `Tap stores below to filter (tap multiple to compare). Counts = shifts this ${view === "month" ? "month" : view === "week" ? "week" : "view"}${templates.length > 0 ? "; red dot = missing shifts vs your saved template" : ""}.`
                  : selectedLocationIds.size === 1
                    ? `Showing ${locationName(singleSelectedId)}. Drag employees onto a day, or click any day to add a shift.`
                    : `Comparing ${selectedLocationIds.size} stores side-by-side. Tap a store again to remove it.`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isAdminOrManager && view === "week" && (
                <Button
                  variant="outline"
                  onClick={() => setApplyTemplateOpen(true)}
                  data-testid="apply-template-btn"
                  className="h-9"
                  disabled={templates.length === 0}
                  title={templates.length === 0 ? "Save a week as a template first" : "Auto-fill this week from a template"}
                >
                  <Wand2 size={14} className="mr-1" /> Apply template
                </Button>
              )}
              {isAdminOrManager && view === "week" && (
                <Button
                  variant="outline"
                  onClick={() => setSaveTemplateOpen(true)}
                  data-testid="save-template-btn"
                  className="h-9"
                >
                  <Save size={14} className="mr-1" /> Save as template
                </Button>
              )}
              {isAdminOrManager && view === "week" && (
                <Button variant="outline" onClick={copyWeek} data-testid="copy-week-btn" className="h-9">
                  <Copy size={14} className="mr-1" /> Copy week
                </Button>
              )}
              {isAdminOrManager && view === "week" && (
                <Button
                  variant="outline"
                  onClick={openEmailDialog}
                  disabled={emailing}
                  data-testid="email-week-btn"
                  className="h-9"
                >
                  <Mail size={14} className="mr-1" /> {emailing ? "Emailing…" : "Email to team"}
                </Button>
              )}
              <Button variant="outline" onClick={printSchedule} data-testid="print-btn" className="h-9">
                <Printer size={14} className="mr-1" /> Print
              </Button>
              {isAdminOrManager && (
                <Button
                  data-testid="add-shift-btn"
                  className="bg-[#0055FF] hover:bg-[#0044CC] h-9"
                  onClick={() => openNew()}
                >
                  <Plus size={14} className="mr-1" /> New shift
                </Button>
              )}
            </div>
          </div>

          {/* Filters + view toggle */}
          <div className="flex items-center gap-3 mb-6 flex-wrap no-print">
            {(isAdmin || (isManager && (user.managed_location_ids || []).length > 1)) && (
              <div className="flex items-center gap-2 flex-1 min-w-[280px]" data-testid="location-pills">
                <MapPin size={14} className="text-gray-400 shrink-0" />
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    data-testid="location-pill-all"
                    onClick={() => setSelectedLocationIds(new Set())}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                      isAllSelected
                        ? "bg-[#0055FF] text-white border-[#0055FF]"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[#0055FF] hover:text-[#0055FF]"
                    }`}
                  >
                    {isAdmin ? "All locations" : "All my locations"}
                    {totalShiftCount > 0 && (
                      <span
                        className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${
                          isAllSelected ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {totalShiftCount}
                      </span>
                    )}
                  </button>
                  {(isAdmin ? locations : manageableLocations).map((l) => {
                    const count = countsPerLocation.get(l.id) || 0;
                    const gaps = gapsPerLocation.get(l.id) || 0;
                    const active = selectedLocationIds.has(l.id);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        data-testid={`location-pill-${l.id}`}
                        onClick={() => toggleLocationId(l.id)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                          active
                            ? "bg-[#0055FF] text-white border-[#0055FF]"
                            : gaps > 0
                              ? "bg-white text-gray-800 border-red-200 hover:border-red-400"
                              : count > 0
                                ? "bg-white text-gray-700 border-gray-200 hover:border-[#0055FF] hover:text-[#0055FF]"
                                : "bg-white text-gray-400 border-gray-200 hover:border-[#0055FF] hover:text-[#0055FF]"
                        }`}
                        title={
                          gaps > 0
                            ? `${gaps} missing shift${gaps === 1 ? "" : "s"} vs your saved template`
                            : (l.address || l.name)
                        }
                      >
                        {gaps > 0 && (
                          <span
                            data-testid={`location-pill-gap-${l.id}`}
                            className={`w-1.5 h-1.5 rounded-full ${active ? "bg-red-200" : "bg-red-500"}`}
                            aria-label="Coverage gaps"
                          />
                        )}
                        {l.name}
                        {count > 0 && (
                          <span
                            data-testid={`location-pill-count-${l.id}`}
                            className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${
                              active ? "bg-white/20 text-white" : "bg-blue-50 text-[#0055FF]"
                            }`}
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <Tabs value={view} onValueChange={setView} className="ml-auto">
              <TabsList>
                <TabsTrigger value="list" data-testid="tab-list">
                  <ListIcon size={14} className="mr-1" /> List
                </TabsTrigger>
                <TabsTrigger value="week" data-testid="tab-week">
                  <CalendarIcon size={14} className="mr-1" /> Week
                </TabsTrigger>
                <TabsTrigger value="month" data-testid="tab-month">
                  <CalendarIcon size={14} className="mr-1" /> Month
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* Employee pool sidebar */}
            {isAdminOrManager && (
              <aside className="space-y-3 no-print" data-testid="employee-pool">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-600">
                      <UsersIcon size={12} /> Employee pool
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-[#0055FF] hover:bg-blue-50 hover:text-[#0055FF]"
                      onClick={openEmpNew}
                      data-testid="add-employee-btn"
                      aria-label="Add employee"
                    >
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </div>
                  <div className="px-3 pt-2">
                    <Input
                      type="search"
                      placeholder="Search teammates…"
                      value={empSearch}
                      onChange={(e) => setEmpSearch(e.target.value)}
                      data-testid="emp-search"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="p-2 space-y-1.5 max-h-[55vh] overflow-y-auto" data-testid="employee-pool-list">
                    {filteredEmployees.length === 0 && (
                      <div className="text-center py-6 px-3">
                        <UsersIcon size={20} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-xs text-gray-500 mb-3">
                          {isAllSelected && manageableLocations.length > 1
                            ? "Pick a single location, then add teammates here."
                            : "No teammates yet — add them once and drag onto shifts."}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={openEmpNew}
                          data-testid="add-employee-empty-btn"
                          className="h-8"
                        >
                          <Plus size={14} className="mr-1" /> Add employee
                        </Button>
                      </div>
                    )}
                    {filteredEmployees.length > 0 && (() => {
                      // Group employees by location_id (in the order locations appear in the array)
                      const byLoc = new Map();
                      for (const e of filteredEmployees) {
                        if (!byLoc.has(e.location_id)) byLoc.set(e.location_id, []);
                        byLoc.get(e.location_id).push(e);
                      }
                      // Show groups in the order of `locations` for predictable display
                      const ordered = locations
                        .map((l) => [l, byLoc.get(l.id) || []])
                        .filter(([, list]) => list.length > 0);
                      return ordered.map(([loc, list]) => {
                        const isCollapsed = collapsedGroups.has(loc.id);
                        return (
                          <div key={loc.id} className="space-y-1" data-testid={`emp-group-${loc.id}`}>
                            <button
                              type="button"
                              data-testid={`emp-group-toggle-${loc.id}`}
                              onClick={() => toggleGroupCollapsed(loc.id)}
                              className="w-full flex items-center justify-between px-1 pt-2 first:pt-0 group/header hover:text-[#0055FF]"
                              aria-expanded={!isCollapsed}
                            >
                              <span className="flex items-center gap-1.5">
                                <ChevronDown
                                  size={10}
                                  className={`text-gray-400 group-hover/header:text-[#0055FF] transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                                />
                                <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500 group-hover/header:text-[#0055FF] font-semibold">
                                  {loc.name}
                                </span>
                              </span>
                              <span className="text-[10px] tabular-nums text-gray-400">{list.length}</span>
                            </button>
                            {!isCollapsed && list.map((e) => (
                              <EmployeeChip
                                key={e.id}
                                employee={e}
                                onEdit={openEmpEdit}
                                isAdminOrManager={isAdminOrManager}
                              />
                            ))}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Hours summary */}
                {hoursSummary.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="hours-summary">
                    <div className="px-4 py-3 border-b border-gray-100 text-xs uppercase tracking-[0.2em] text-gray-600">
                      Hours this {view === "month" ? "month" : view === "week" ? "week" : "range"}
                    </div>
                    <ul className="p-2 space-y-1">
                      {hoursSummary.map((row) => (
                        <li key={row.id || row.name} className="flex items-center gap-2 px-2 py-1 text-sm">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: row.color }} />
                          <span className="flex-1 truncate">{row.name}</span>
                          <span className="tabular-nums text-gray-600">{row.hours.toFixed(1)}h</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </aside>
            )}

            {/* MAIN CONTENT */}
            <div>
              {/* LIST VIEW */}
              {view === "list" && (
                <div className="space-y-4" data-testid="list-view">
                  {loading && <p className="text-sm text-gray-500">Loading…</p>}
                  {!loading && sortedDays.length === 0 && (
                    <div className="text-center bg-white border border-dashed border-gray-300 rounded-lg p-10">
                      <CalendarIcon size={28} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-sm text-gray-600">No shifts scheduled yet.</p>
                      {isAdminOrManager && (
                        <Button onClick={() => openNew()} variant="outline" className="mt-3">
                          <Plus size={14} className="mr-1" /> Add the first shift
                        </Button>
                      )}
                    </div>
                  )}
                  {!loading && sortedDays.map((day) => {
                    const list = shiftsByDay.get(day);
                    const dateObj = new Date(`${day}T00:00:00`);
                    return (
                      <section key={day} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`day-${day}`}>
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                          <h3 className="font-display font-semibold tracking-tight">
                            {dateObj.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
                          </h3>
                          <p className="text-xs text-gray-500">{list.length} shift{list.length === 1 ? "" : "s"}</p>
                        </div>
                        <ul className="divide-y divide-gray-100">
                          {list.map((s) => {
                            const color = empColor(s) || "#0055FF";
                            return (
                              <li key={s.id} className="px-5 py-4 flex items-center gap-4" data-testid={`shift-row-${s.id}`}>
                                <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: color }} />
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold tracking-tight">
                                    {s.employee_name}{s.role ? <span className="text-gray-500 font-normal"> · {s.role}</span> : null}
                                  </p>
                                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                                    <span className="inline-flex items-center gap-1"><Clock size={12} />{fmtTime(s.start_at)} – {fmtTime(s.end_at)} <span className="text-gray-400">({hoursBetween(s.start_at, s.end_at).toFixed(1)}h)</span></span>
                                    <span className="inline-flex items-center gap-1"><MapPin size={12} />{s.location_name || locationName(s.location_id)}</span>
                                    {s.notes && <span className="inline-flex items-center gap-1"><StickyNote size={12} />{s.notes}</span>}
                                  </div>
                                </div>
                                {canManageAt(s.location_id) && (
                                  <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#0055FF]" onClick={() => openEdit(s)} data-testid={`edit-shift-${s.id}`} aria-label="Edit shift">
                                      <Pencil size={14} />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" onClick={() => remove(s)} data-testid={`delete-shift-${s.id}`} aria-label="Delete shift">
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    );
                  })}
                </div>
              )}

              {/* WEEK VIEW */}
              {view === "week" && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="week-view">
                  <div className="px-5 py-3 flex items-center justify-between border-b border-gray-100 no-print">
                    <h3 className="font-display font-semibold tracking-tight">{weekLabel}</h3>
                    <div className="flex items-center gap-1">
                      {selectedLocationIds.size >= 2 && (
                        <Button
                          variant={splitByStore ? "default" : "outline"}
                          size="sm"
                          className={`h-8 mr-2 ${splitByStore ? "bg-[#0055FF] hover:bg-[#0044CC]" : ""}`}
                          onClick={() => setSplitByStore((v) => !v)}
                          data-testid="toggle-split-by-store"
                          title="Show one row per selected store"
                        >
                          {splitByStore ? "Split by store" : "Merged"}
                        </Button>
                      )}
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWeekAnchor(addDays(weekAnchor, -7))} data-testid="week-prev" aria-label="Previous week">
                        <ChevronLeft size={14} />
                      </Button>
                      <Button variant="outline" size="sm" className="h-8" onClick={() => setWeekAnchor(startOfWeek(new Date()))} data-testid="week-today">
                        This week
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWeekAnchor(addDays(weekAnchor, 7))} data-testid="week-next" aria-label="Next week">
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>

                  {/* Day-of-week header */}
                  <div className={`grid ${selectedLocationIds.size >= 2 && splitByStore ? "grid-cols-[160px_repeat(7,1fr)]" : "grid-cols-7"} text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-100`}>
                    {selectedLocationIds.size >= 2 && splitByStore && (
                      <div className="px-2 py-2 text-center border-r border-gray-100">Store</div>
                    )}
                    {weekCells.map((d) => (
                      <div key={d.toISOString()} className="px-2 py-2 text-center">
                        <p>{d.toLocaleDateString([], { weekday: "short" })}</p>
                        <p className="text-gray-700 text-sm font-semibold">{d.getDate()}</p>
                      </div>
                    ))}
                  </div>

                  {/* SPLIT-BY-STORE: one row per selected store */}
                  {selectedLocationIds.size >= 2 && splitByStore ? (
                    <div data-testid="week-split-mode">
                      {[...selectedLocationIds].map((laneLocId) => {
                        const laneLoc = locations.find((x) => x.id === laneLocId);
                        if (!laneLoc) return null;
                        return (
                          <div key={laneLocId} className="grid grid-cols-[160px_repeat(7,1fr)] auto-rows-[200px] border-b border-gray-100 last:border-b-0">
                            <div className="px-3 py-2 border-r border-gray-100 bg-gray-50/50 flex flex-col justify-center" data-testid={`week-lane-${laneLocId}`}>
                              <p className="font-display font-semibold text-sm tracking-tight">{laneLoc.name}</p>
                              <p className="text-[10px] text-gray-500 tabular-nums">
                                {filteredShifts.filter((s) => s.location_id === laneLocId).length} shift{filteredShifts.filter((s) => s.location_id === laneLocId).length === 1 ? "" : "s"}
                              </p>
                            </div>
                            {weekCells.map((cell) => {
                              const key = ymdLocal(cell);
                              const dayShifts = (shiftsByDay.get(key) || []).filter((s) => s.location_id === laneLocId);
                              const isToday = key === today;
                              return (
                                <DayCell key={key} date={cell} dayKey={key} isToday={isToday} canManage={isAdminOrManager} forLocationId={laneLocId}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className={`text-[10px] tabular-nums ${isToday ? "font-bold text-[#0055FF]" : "text-gray-500"}`}>
                                      {dayShifts.length} shift{dayShifts.length === 1 ? "" : "s"}
                                    </span>
                                    {isAdminOrManager && canManageAt(laneLocId) && (
                                      <button
                                        type="button"
                                        aria-label="Add shift"
                                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-[#0055FF] transition-opacity"
                                        data-testid={`week-add-${key}-${laneLocId}`}
                                        onClick={() => {
                                          const start = new Date(cell); start.setHours(9, 0, 0, 0);
                                          const end = new Date(cell); end.setHours(17, 0, 0, 0);
                                          openNew({
                                            start_at: isoToLocalInput(start.toISOString()),
                                            end_at: isoToLocalInput(end.toISOString()),
                                            location_id: laneLocId,
                                          });
                                        }}
                                      >
                                        <Plus size={12} />
                                      </button>
                                    )}
                                  </div>
                                  <div className="space-y-1 overflow-y-auto max-h-[160px]">
                                    {dayShifts.map((s) => (
                                      <ShiftChip
                                        key={s.id}
                                        shift={s}
                                        employeeColor={empColor(s)}
                                        onClick={(sh) => canManageAt(sh.location_id) && openEdit(sh)}
                                        canDrag={canManageAt(s.location_id)}
                                      />
                                    ))}
                                  </div>
                                </DayCell>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* MERGED week grid */
                    <div className="grid grid-cols-7 auto-rows-[300px]">
                      {weekCells.map((cell) => {
                        const key = ymdLocal(cell);
                        const dayShifts = shiftsByDay.get(key) || [];
                        const isToday = key === today;
                        return (
                          <DayCell key={key} date={cell} dayKey={key} isToday={isToday} canManage={isAdminOrManager}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-[10px] tabular-nums ${isToday ? "font-bold text-[#0055FF]" : "text-gray-500"}`}>
                                {dayShifts.length} shift{dayShifts.length === 1 ? "" : "s"}
                              </span>
                              {isAdminOrManager && (
                                <button
                                  type="button"
                                  aria-label="Add shift on this day"
                                  data-testid={`week-add-${key}`}
                                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-[#0055FF] transition-opacity"
                                  onClick={() => {
                                    const start = new Date(cell); start.setHours(9, 0, 0, 0);
                                    const end = new Date(cell); end.setHours(17, 0, 0, 0);
                                    openNew({ start_at: isoToLocalInput(start.toISOString()), end_at: isoToLocalInput(end.toISOString()) });
                                  }}
                                >
                                  <Plus size={12} />
                                </button>
                              )}
                            </div>
                            <div className="space-y-1 overflow-y-auto max-h-[260px]">
                              {dayShifts.map((s) => (
                                <ShiftChip
                                  key={s.id}
                                  shift={s}
                                  employeeColor={empColor(s)}
                                  onClick={(sh) => canManageAt(sh.location_id) && openEdit(sh)}
                                  canDrag={canManageAt(s.location_id)}
                                />
                              ))}
                            </div>
                          </DayCell>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* MONTH VIEW */}
              {view === "month" && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="month-view">
                  <div className="px-5 py-3 flex items-center justify-between border-b border-gray-100 no-print">
                    <h3 className="font-display font-semibold tracking-tight">{monthLabel}</h3>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} data-testid="cal-prev" aria-label="Previous month">
                        <ChevronLeft size={14} />
                      </Button>
                      <Button variant="outline" size="sm" className="h-8" onClick={() => { const d = new Date(); setMonthAnchor(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="cal-today">
                        Today
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} data-testid="cal-next" aria-label="Next month">
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-gray-100">
                    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                      <div key={d} className="px-2 py-2 text-center">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 auto-rows-[120px]">
                    {monthCells.map((cell, idx) => {
                      if (!cell) return <div key={`empty-${idx}`} className="border-b border-r border-gray-100 bg-gray-50/30" />;
                      const key = ymdLocal(cell);
                      const dayShifts = shiftsByDay.get(key) || [];
                      const isToday = key === today;
                      return (
                        <DayCell key={key} date={cell} dayKey={key} isToday={isToday} canManage={isAdminOrManager}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs tabular-nums ${isToday ? "font-bold text-[#0055FF]" : "text-gray-500"}`}>{cell.getDate()}</span>
                            {isAdminOrManager && (
                              <button
                                type="button"
                                aria-label="Add shift on this day"
                                data-testid={`cal-add-${key}`}
                                className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-[#0055FF] transition-opacity"
                                onClick={() => {
                                  const start = new Date(cell); start.setHours(9, 0, 0, 0);
                                  const end = new Date(cell); end.setHours(17, 0, 0, 0);
                                  openNew({ start_at: isoToLocalInput(start.toISOString()), end_at: isoToLocalInput(end.toISOString()) });
                                }}
                              >
                                <Plus size={12} />
                              </button>
                            )}
                          </div>
                          <div className="space-y-1">
                            {dayShifts.slice(0, 3).map((s) => (
                              <ShiftChip
                                key={s.id}
                                shift={s}
                                employeeColor={empColor(s)}
                                onClick={(sh) => canManageAt(sh.location_id) && openEdit(sh)}
                                canDrag={canManageAt(s.location_id)}
                              />
                            ))}
                            {dayShifts.length > 3 && (
                              <p className="text-[10px] text-gray-500 px-1">+{dayShifts.length - 3} more</p>
                            )}
                          </div>
                        </DayCell>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Shift dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent data-testid="shift-dialog" className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">
                {editing.id ? "Edit shift" : "New shift"}
              </DialogTitle>
              <DialogDescription>
                Pick an employee from the pool or type a name.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Employee</Label>
                <Select
                  value={editing.employee_id || "__custom"}
                  onValueChange={(v) => {
                    if (v === "__custom") {
                      setEditing({ ...editing, employee_id: null });
                    } else {
                      const e = employees.find((x) => x.id === v);
                      if (e) setEditing({ ...editing, employee_id: e.id, employee_name: e.name, role: e.role || editing.role, location_id: e.location_id });
                    }
                  }}
                >
                  <SelectTrigger data-testid="shift-employee-select"><SelectValue placeholder="Pick from pool…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__custom">— Type a name —</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name}{e.role ? ` · ${e.role}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="shift-employee"
                  value={editing.employee_name}
                  onChange={(e) => setEditing({ ...editing, employee_name: e.target.value, employee_id: null })}
                  placeholder="Or type a name (e.g. fill-in helper)"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Role</Label>
                  <Input data-testid="shift-role" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} placeholder="Cashier, manager…" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Location</Label>
                  <Select value={editing.location_id} onValueChange={(v) => setEditing({ ...editing, location_id: v })}>
                    <SelectTrigger data-testid="shift-location"><SelectValue placeholder="Choose a location" /></SelectTrigger>
                    <SelectContent>
                      {manageableLocations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Quick set</Label>
                  {isAdminOrManager && (
                    <button
                      type="button"
                      data-testid="open-save-preset"
                      onClick={() => setSavePresetOpen(true)}
                      className="text-[11px] text-[#0055FF] hover:underline"
                      disabled={!editing.start_at || !editing.end_at}
                    >
                      + Save as preset
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="preset-pills">
                  {presets.map((p) => (
                    <span key={p.id} className="inline-flex items-center group">
                      <button
                        type="button"
                        data-testid={`preset-${p.id}`}
                        onClick={() => applyPreset(p)}
                        className="text-xs px-2.5 py-1 rounded-l-full border border-gray-300 bg-white text-gray-700 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors tabular-nums"
                      >
                        {p.name}
                      </button>
                      {isAdminOrManager && (
                        <button
                          type="button"
                          aria-label={`Delete ${p.name}`}
                          data-testid={`preset-delete-${p.id}`}
                          onClick={() => deletePreset(p)}
                          className="px-1.5 py-1 rounded-r-full border border-l-0 border-gray-300 bg-white text-gray-300 hover:text-red-600 hover:border-red-200 transition-colors"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Start</Label>
                  <Input type="datetime-local" data-testid="shift-start" value={editing.start_at} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">End</Label>
                  <Input type="datetime-local" data-testid="shift-end" value={editing.end_at} onChange={(e) => setEditing({ ...editing, end_at: e.target.value })} />
                </div>
              </div>

              {editing.start_at && editing.end_at && new Date(editing.end_at) > new Date(editing.start_at) && (
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Clock size={12} />{hoursBetween(editing.start_at, editing.end_at).toFixed(1)} hours</p>
              )}

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Notes</Label>
                <Textarea data-testid="shift-notes" rows={3} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Opening shift, off-Tuesdays, etc." />
              </div>

              {/* Conflict warning */}
              {(() => {
                if (!editing.start_at || !editing.end_at) return null;
                const startISO = localToISO(editing.start_at);
                const endISO = localToISO(editing.end_at);
                if (new Date(endISO) <= new Date(startISO)) return null;
                const key = editing.employee_id || editing.employee_name?.trim();
                if (!key) return null;
                const c = findConflict(key, startISO, endISO, editing.id);
                if (!c) return null;
                return (
                  <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="conflict-warning">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                    <span>
                      {editing.employee_name} already has a shift at <strong>{c.location_name}</strong> from {fmtTime(c.start_at)} to {fmtTime(c.end_at)} that overlaps this one.
                    </span>
                  </div>
                );
              })()}
            </div>
            <DialogFooter className="gap-2">
              {editing.id && (
                <Button variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700 mr-auto" onClick={() => { setDialogOpen(false); const found = shifts.find((x) => x.id === editing.id); if (found) remove(found); }} data-testid="shift-delete-in-dialog">
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC]" onClick={save} disabled={saving} data-testid="save-shift">
                {editing.id ? "Save changes" : "Add shift"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Employee dialog */}
        <Dialog open={empOpen} onOpenChange={setEmpOpen}>
          <DialogContent data-testid="employee-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">
                {empForm.id ? "Edit employee" : "Add to pool"}
              </DialogTitle>
              <DialogDescription>
                {empForm.id ? "Update this teammate's details." : `Add a new teammate to the ${locationName(singleSelectedId || manageableLocations[0]?.id)} pool.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Name</Label>
                <Input data-testid="emp-name" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} placeholder="Jane Doe" />
              </div>
              {manageableLocations.length > 1 && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Location</Label>
                  <Select value={empForm.location_id} onValueChange={(v) => setEmpForm({ ...empForm, location_id: v })}>
                    <SelectTrigger data-testid="emp-location"><SelectValue placeholder="Pick a location" /></SelectTrigger>
                    <SelectContent>
                      {manageableLocations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Role</Label>
                  <Input data-testid="emp-role" value={empForm.role} onChange={(e) => setEmpForm({ ...empForm, role: e.target.value })} placeholder="Cashier" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Phone</Label>
                  <Input data-testid="emp-phone" value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Email</Label>
                <Input
                  type="email"
                  data-testid="emp-email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={empForm.email}
                  onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })}
                  placeholder="jane@example.com"
                />
                <p className="text-[11px] text-gray-500">Used for sending them their schedule via email.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Color</Label>
                <div className="flex flex-wrap gap-2">
                  {["#0055FF","#059669","#D97706","#DC2626","#7C3AED","#0891B2","#DB2777","#65A30D","#EA580C","#4F46E5","#0D9488","#C026D3"].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEmpForm({ ...empForm, color: c })}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${empForm.color === c ? "border-gray-900 scale-110" : "border-white"}`}
                      style={{ background: c }}
                      aria-label={`Color ${c}`}
                      data-testid={`emp-color-${c.slice(1)}`}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Notes</Label>
                <Textarea rows={2} data-testid="emp-notes" value={empForm.notes} onChange={(e) => setEmpForm({ ...empForm, notes: e.target.value })} placeholder="Availability, etc." />
              </div>
            </div>
            <DialogFooter className="gap-2">
              {empForm.id && (
                <Button variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700 mr-auto" onClick={removeEmp} data-testid="emp-delete">
                  <Trash2 size={14} className="mr-1" /> Remove
                </Button>
              )}
              <Button variant="outline" onClick={() => setEmpOpen(false)} disabled={empSaving}>
                <X size={14} className="mr-1" /> Cancel
              </Button>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC]" onClick={saveEmp} disabled={empSaving} data-testid="save-emp">
                {empForm.id ? "Save" : "Add"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Email recipient picker */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent data-testid="email-recipients-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">Email this week's schedule</DialogTitle>
              <DialogDescription>
                Pick which teammates to email. Only employees with shifts <strong>this week</strong> and an email on file are listed.
              </DialogDescription>
            </DialogHeader>

            {emailCandidates.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">
                <Mail size={24} className="mx-auto text-gray-300 mb-2" />
                Nobody to email this week — either there are no shifts, or no scheduled employee has an email on file.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs text-gray-600 px-1">
                  <span data-testid="email-recipients-count">
                    {Object.values(emailRecipients).filter(Boolean).length} of {emailCandidates.length} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="email-select-all"
                      className="text-[#0055FF] hover:underline"
                      onClick={() => {
                        const all = {};
                        for (const c of emailCandidates) all[c.id] = true;
                        setEmailRecipients(all);
                      }}
                    >
                      Select all
                    </button>
                    <span className="text-gray-300">·</span>
                    <button
                      type="button"
                      data-testid="email-select-none"
                      className="text-[#0055FF] hover:underline"
                      onClick={() => setEmailRecipients({})}
                    >
                      None
                    </button>
                  </div>
                </div>
                <div className="max-h-[40vh] overflow-y-auto rounded-md border border-gray-200">
                  <ul className="divide-y divide-gray-100">
                    {emailCandidates.map((c) => {
                      const checked = !!emailRecipients[c.id];
                      return (
                        <li key={c.id}>
                          <label
                            data-testid={`email-recipient-${c.id}`}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 select-none"
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-[#0055FF]"
                              checked={checked}
                              onChange={(e) => setEmailRecipients({ ...emailRecipients, [c.id]: e.target.checked })}
                            />
                            <span
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ background: c.color }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{c.name}</p>
                              <p className="text-[11px] text-gray-500 truncate">{c.email} · {c.shifts.length} shift{c.shifts.length === 1 ? "" : "s"}</p>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={emailing}>
                Cancel
              </Button>
              <Button
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                onClick={emailWeek}
                disabled={emailing || Object.values(emailRecipients).filter(Boolean).length === 0}
                data-testid="send-emails-confirm"
              >
                <Mail size={14} className="mr-1" />
                {emailing
                  ? "Sending…"
                  : `Send to ${Object.values(emailRecipients).filter(Boolean).length || "—"}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save preset mini-dialog */}
        <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
          <DialogContent data-testid="save-preset-dialog" className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">Save shift preset</DialogTitle>
              <DialogDescription>
                Give this time range a name so you can re-use it with one click.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Preset name</Label>
              <Input
                data-testid="preset-name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g. Opening (7–3)"
                autoFocus
              />
              {editing.start_at && editing.end_at && (
                <p className="text-xs text-gray-500">
                  Time: {fmtTime(localToISO(editing.start_at))} – {fmtTime(localToISO(editing.end_at))}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSavePresetOpen(false)}>Cancel</Button>
              <Button
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                onClick={saveCurrentAsPreset}
                data-testid="save-preset-confirm"
              >
                Save preset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save week as template */}
        <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
          <DialogContent data-testid="save-template-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">Save week as template</DialogTitle>
              <DialogDescription>
                Snapshot every shift in <strong>{weekLabel}</strong>{singleSelectedId ? ` at ${locationName(singleSelectedId)}` : " across all your locations"}. You can re-apply this template to any future week.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Template name</Label>
              <Input
                data-testid="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Standard week"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveTemplateOpen(false)} disabled={templateSaving}>Cancel</Button>
              <Button
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                onClick={saveTemplate}
                disabled={templateSaving}
                data-testid="save-template-confirm"
              >
                Save template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Apply template */}
        <Dialog open={applyTemplateOpen} onOpenChange={setApplyTemplateOpen}>
          <DialogContent data-testid="apply-template-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight text-2xl">Apply template</DialogTitle>
              <DialogDescription>
                Auto-fill <strong>{weekLabel}</strong> using a saved week template.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Template</Label>
                <Select value={chosenTemplateId} onValueChange={setChosenTemplateId}>
                  <SelectTrigger data-testid="template-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} · {t.entries.length} shift{t.entries.length === 1 ? "" : "s"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {chosenTemplateId && (
                  <button
                    type="button"
                    data-testid="delete-template-btn"
                    onClick={() => {
                      const t = templates.find((x) => x.id === chosenTemplateId);
                      if (t) deleteTemplate(t);
                    }}
                    className="text-[11px] text-red-600 hover:underline mt-1"
                  >
                    Delete this template
                  </button>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="apply-overwrite"
                  checked={applyOverwrite}
                  onChange={(e) => setApplyOverwrite(e.target.checked)}
                  className="h-4 w-4"
                />
                <span>Replace any existing shifts in this week first</span>
              </label>
              <p className="text-xs text-gray-500">
                Otherwise template shifts will be added alongside what's already scheduled.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyTemplateOpen(false)} disabled={applying}>Cancel</Button>
              <Button
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                onClick={applyTemplate}
                disabled={applying || !chosenTemplateId}
                data-testid="apply-template-confirm"
              >
                {applying ? "Applying…" : "Auto-fill week"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Layout>
    </DndContext>
  );
}
